// DashboardIndicatorsStyle.js
import { styled } from '@mui/system';
import { Box } from '@mui/material';

/* ─── Root container — scrollable grid layout ─── */
export const DashboardContainer = styled('div')(({ isSidebarOpen }) => ({
    position: 'relative',
    marginLeft: isSidebarOpen ? '75px' : '60px',
    width: `calc(100% - ${isSidebarOpen ? '75px' : '60px'})`,
    minHeight: '100vh',
    backgroundColor: '#F8FAFC',
    padding: '8px 24px 16px',
    boxSizing: 'border-box',
    transition: 'margin-left 0.3s, width 0.3s',
    '@media (max-width: 768px)': {
        padding: '12px 12px',
    },
}));

/* ─── KPI grid: auto-fill responsive cards ─── */
export const KpiGrid = styled('div')(() => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
    '@media (min-width: 1440px)': {
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    },
    '@media (max-width: 768px)': {
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '12px',
    },
    '@media (max-width: 480px)': {
        gridTemplateColumns: '1fr',
    },
}));

/* ─── Primary charts: 2-column grid (wide charts) ─── */
export const PrimaryChartsGrid = styled('div')(() => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
    marginBottom: '24px',
    '@media (max-width: 1024px)': { gridTemplateColumns: '1fr' },
}));

/* ─── Secondary charts: 3-column grid (standard charts) ─── */
export const SecondaryChartsGrid = styled('div')(() => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    marginBottom: '24px',
    '@media (max-width: 1200px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
    '@media (max-width: 768px)': { gridTemplateColumns: '1fr' },
}));

/* ─── Section header bar (clickable, collapsible) ─── */
export const SectionTitle = styled('div')(({ theme }) => ({
    background: 'linear-gradient(135deg, #2d3748 0%, #3d4a5c 100%)',
    color: '#fff',
    padding: '10px 16px',
    borderRadius: '6px',
    fontWeight: 700,
    fontSize: '0.9rem',
    letterSpacing: 0.3,
    marginBottom: '12px',
    textAlign: 'center',
    textTransform: 'uppercase',
    position: 'relative',
    overflow: 'hidden',
    cursor: 'pointer',
    userSelect: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'filter 0.15s ease',
    '&:hover': {
        filter: 'brightness(1.1)',
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '4px',
        background: theme?.palette?.primary?.main || '#ff9900',
        borderRadius: '2px',
    },
}));

export const SectionChevron = styled('span')(({ collapsed }) => ({
    position: 'absolute',
    right: 12,
    display: 'inline-flex',
    alignItems: 'center',
    transition: 'transform 0.25s ease',
    transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
    '& .MuiSvgIcon-root': {
        fontSize: 20,
        color: 'rgba(255,255,255,0.8)',
    },
}));

export const SectionContent = styled('div')(({ collapsed }) => ({
    overflow: 'hidden',
    maxHeight: collapsed ? 0 : 5000,
    opacity: collapsed ? 0 : 1,
    transition: 'max-height 0.35s ease, opacity 0.25s ease',
}));

/* ─── Section wrapper ─── */
export const SectionContainer = styled('div')(() => ({
    width: '100%',
    marginBottom: '20px',
}));

/* ─── Half-width section container for Totales / Promedios side-by-side ─── */
export const SectionContainerHalf = styled('div')(() => ({
    flex: '0 0 50%',
    maxWidth: '50%',
    padding: '0 8px',
    boxSizing: 'border-box',
    marginBottom: '16px',
    '@media (max-width: 1024px)': {
        flex: '0 0 100%',
        maxWidth: '100%',
    },
}));

/* ─── Inline area chart pair row (AA/FR pie + bar) ─── */
export const AreaChartsRow = styled('div')(() => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
    marginTop: '12px',
    '@media (max-width: 768px)': { gridTemplateColumns: '1fr' },
}));

/* ─── "In progress" fallback ─── */
export const InProgressMessage = styled('div')(() => ({
    color: '#94A3B8',
    textAlign: 'center',
    marginTop: '100px',
    fontSize: '1.25rem',
    fontWeight: 600,
    padding: '20px',
}));

/* ─── Total análisis badge ─── */
export const TotalAnalisisTab = styled('div')(() => ({
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
    fontSize: '0.95rem',
    color: '#334155',
    flexWrap: 'wrap',
}));

/* ─── Floating tooltip ─── */
export const TooltipContainer = styled('div')(({ top, left }) => ({
    position: 'absolute',
    backgroundColor: '#333',
    color: '#fff',
    padding: '5px 10px',
    borderRadius: '5px',
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
    whiteSpace: 'nowrap',
    zIndex: 1200,
    fontSize: '0.9em',
    pointerEvents: 'none',
    top: top,
    left: left,
}));

/* ─── Modal ─── */
export const ModalContent = styled(Box)(() => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '300px',
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    textAlign: 'center',
    zIndex: 1300,
    animation: 'fadeIn 0.5s ease-in-out',
    '@keyframes fadeIn': {
        from: { opacity: 0, transform: 'translate(-50%, -60%)' },
        to: { opacity: 1, transform: 'translate(-50%, -50%)' },
    },
}));
